// 물약 퀵슬롯 버튼(D2c, PRD 4.6 "퀵슬롯 2칸"): HP(R)·MP(T). PC는 하단 스킬 바 왼쪽 끝(바 안의 칸), 터치는 회피 버튼 왼쪽 화면 아래 가장자리.
// 플레이 영역(영웅·앞길·위험물) 위에는 두지 않는다. 공용 쿨다운 원형 스윕·남은 초·보유 수·연속 감쇠(다음 회복 %)를 보인다.
// 누르면 InputSink로 Btn.Potion1/2를 보낸다(키보드 R/T와 같은 입력, 고정 스텝이 판정). 값이 바뀔 때만 DOM을 쓴다(문자열 표 재사용).
// 거부(쿨다운·없음·가득) 흔들림은 움직임 줄이기(브라우저)·섬광 약화(게임 설정)에서 테두리 색만 잠깐 바꾼다.
import { Ev, type GameEvent } from '../shared/events';
import { POTIONS } from '../shared/content/consumables';
import { Btn, Scene, combatScene, type BtnBit, type GameView, type InputSink } from '../shared/types';
import { C, FONT, FONT_NUM, TEXT_OUTLINE, el } from './dom';
import { assetUrl } from '../pwa/assetMap';
import { CD_SWEEP, CD_TEXT, DIM_FILTER, cdTenths, reducedMotion, sweepStep } from './skillbar';

/** 물약 병 아이콘(흰 외곽 + 색 액체). color = 액체 색 */
function flaskSvg(liquid: string, hi: string): string {
  return '<svg viewBox="0 0 64 64" width="100%" height="100%" style="display:block;filter:drop-shadow(0 2px 1px rgba(0,0,0,.6))">' +
    '<rect x="25" y="5" width="14" height="7" rx="2" fill="#c89a5a" stroke="#2a1608" stroke-width="2"/>' +
    '<path d="M27 12 h10 v9 c9 4 15 12 15 21 c0 11 -9 18 -20 18 s-20 -7 -20 -18 c0 -9 6 -17 15 -21 z" fill="#e8f0ff" fill-opacity=".25" stroke="#fff" stroke-width="3" stroke-linejoin="round"/>' +
    '<path d="M15 40 c0 -2 2 -4 5 -4 c6 2 18 2 24 0 c3 0 5 2 5 4 c0 9 -7 15 -17 15 s-17 -6 -17 -15 z" fill="' + liquid + '"/>' +
    '<ellipse cx="24" cy="42" rx="4" ry="2.5" fill="' + hi + '" opacity=".85"/>' +
    '</svg>';
}
const SLOTS: readonly { bit: BtnBit; key: string; name: string; liquid: string; hi: string; ring: string; art: string }[] = [
  { bit: Btn.Potion1, key: 'R', name: 'HP 물약', liquid: '#e8243a', hi: '#ffb0b8', ring: '#ff6a6a', art: 'hp_s' },
  { bit: Btn.Potion2, key: 'T', name: 'MP 물약', liquid: '#2a74e8', hi: '#b0d4ff', ring: '#7ec4ff', art: 'mp_s' },
];
/** 귀환서 칸(PC 스킬 바, 2026-10-27): 누르면 「한 번 더」 표시, 2.5초 안에 다시 누르면 사용(실수로 판을 끝내지 않게). ui/index.ts가 useReturnScroll을 보낸다 */
export const RETURN_SCROLL_EVENT = 'dh-use-return-scroll';
const ARM_MS = 2500;
const DENY_KF: Keyframe[] = [{ transform: 'translateX(0)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(-2px)' }, { transform: 'translateX(0)' }];
const DENY_OPT: KeyframeAnimationOptions = { duration: 220, easing: 'linear' };
const POP_KF: Keyframe[] = [{ transform: 'scale(1.12)' }, { transform: 'scale(1)' }];
const POP_OPT: KeyframeAnimationOptions = { duration: 160, easing: 'ease-out' };
/** 보유 수 문자열 표(0..999) — 프레임마다 새 문자열을 만들지 않는다 */
const COUNT_TEXT: readonly string[] = Array.from({ length: 1000 }, (_, i) => String(i));
/** 감쇠 표시(20·40·60·80%) */
const DECAY_TEXT: Readonly<Record<number, string>> = { 20: '20%', 40: '40%', 60: '60%', 80: '80%' };

interface Btn2 {
  readonly bit: BtnBit;
  readonly root: HTMLDivElement;
  readonly face: HTMLDivElement;
  readonly sweep: HTMLDivElement;
  readonly cd: HTMLDivElement;
  readonly count: HTMLDivElement;
  readonly decay: HTMLDivElement;
  readonly ring: string;
  pointer: number;
  lastSweep: number; lastTenths: number; lastCount: number; lastDecay: number; lastDim: number;
}

export class PotionHud {
  readonly root: HTMLDivElement;
  private readonly btns: Btn2[] = [];
  private cursor: number;
  private shown = -1;
  private readonly onEvent = (e: GameEvent): void => {
    if (e.type === Ev.ConsumeDenied) this.deny(e.b);
    else if (e.type === Ev.Consume && e.a <= 1 && (e.flags & 1) === 0) this.pop(e.a);
  };

  /**
   * mode 'pc': 부모(스킬 바 줄)의 맨 왼쪽 칸(flex 흐름). 'touch': 절대 위치(place로 놓는다).
   * size = 버튼 지름(px, 터치는 place가 다시 정한다)
   */
  constructor(parent: HTMLElement, private readonly view: GameView, private readonly input: InputSink, private readonly mode: 'pc' | 'touch', size = 54) {
    this.root = el('div', mode === 'pc'
      ? 'order:-1;display:flex;align-items:flex-end;gap:8px;margin-right:6px;margin-bottom:20px;pointer-events:none;'
      : 'position:absolute;inset:0;pointer-events:none;', parent);
    this.root.dataset.testid = 'potion-bar';
    for (let i = 0; i < SLOTS.length; i++) this.btns.push(this.make(SLOTS[i], size, i));
    // 귀환서: PC 스킬 바에만(터치는 오른손 호 12칸이 배치 편집기·저장 배치와 묶여 있어 칸을 늘리지 않고 톱니 → 일시정지 창의 「귀환서 사용」으로 둔다)
    if (mode === 'pc') {
      const sc = el('div', 'position:relative;width:' + Math.round(size * 0.82) + 'px;height:' + Math.round(size * 0.82) + 'px;border-radius:12px;box-sizing:border-box;' +
        'border:2.5px solid ' + C.gold + ';background:radial-gradient(circle at 40% 35%,#2c3a66,#0e1428 70%);box-shadow:0 2px 6px rgba(0,0,0,.65),inset 0 0 8px rgba(0,0,0,.6);pointer-events:auto;cursor:pointer;user-select:none;align-self:flex-end;', this.root);
      sc.dataset.testid = 'potion-scroll';
      sc.setAttribute('role', 'button');
      sc.setAttribute('aria-label', '귀환서 (두 번 눌러 사용 · 드롭 수거 후 마을로)');
      sc.setAttribute('data-tip', '귀환서 · 두 번 눌러 사용\n바닥 드롭을 모두 거두고 마을로');
      const im = el('img', 'position:absolute;inset:8%;width:84%;height:84%;object-fit:contain;pointer-events:none;', sc);
      im.alt = ''; im.draggable = false; im.src = assetUrl('ui/items/scroll_return.webp');
      im.onerror = () => { im.remove(); sc.textContent = '⤺'; sc.style.color = '#ffe7a0'; sc.style.font = '900 22px ' + FONT; sc.style.textAlign = 'center'; };
      this.scrollCount = el('div', 'position:absolute;right:-5px;bottom:-4px;min-width:18px;height:16px;padding:0 3px;box-sizing:border-box;border-radius:8px;' +
        'background:linear-gradient(180deg,#26335a,#10162a);border:1.5px solid #e8cd86;font:900 10px ' + FONT_NUM + ';color:#fff;line-height:13px;text-align:center;pointer-events:none;', sc, '0');
      this.scrollCount.dataset.testid = 'potion-scroll-count';
      this.scrollArm = el('div', 'position:absolute;left:50%;bottom:calc(100% + 4px);transform:translateX(-50%);display:none;padding:1px 6px;border-radius:6px;background:#3a1418;border:1px solid #ff9a6a;' +
        'font:800 10px ' + FONT + ';color:#ffd0b0;white-space:nowrap;pointer-events:none;', sc, '한 번 더');
      sc.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        if (this.lastScrolls <= 0) { sc.animate(DENY_KF, DENY_OPT); return; }
        const now = performance.now();
        if (now < this.armedUntil) { this.armedUntil = 0; this.scrollArm.style.display = 'none'; window.dispatchEvent(new CustomEvent(RETURN_SCROLL_EVENT)); return; }
        this.armedUntil = now + ARM_MS; this.scrollArm.style.display = 'block';
      });
      this.scroll = sc;
    }
    this.cursor = view.events.written;
  }
  private scroll: HTMLDivElement | null = null;
  private scrollCount!: HTMLDivElement;
  private scrollArm!: HTMLDivElement;
  private armedUntil = 0;
  private lastScrolls = -1;

  private make(s: (typeof SLOTS)[number], size: number, idx: number): Btn2 {
    const root = el('div', (this.mode === 'pc' ? 'position:relative;' : 'position:absolute;') + 'width:' + size + 'px;height:' + size + 'px;border-radius:50%;box-sizing:border-box;' +
      'border:2.5px solid ' + C.gold + ';background:radial-gradient(circle at 40% 35%,#2c3a66,#0e1428 70%);box-shadow:0 2px 6px rgba(0,0,0,.65),inset 0 0 8px rgba(0,0,0,.6);' +
      'pointer-events:auto;cursor:pointer;touch-action:none;user-select:none;', this.root);
    root.dataset.testid = 'potion-' + idx;
    root.setAttribute('role', 'button');
    root.setAttribute('aria-label', s.name + (this.mode === 'pc' ? ' (' + s.key + ')' : ''));
    const face = el('div', 'position:absolute;inset:8%;pointer-events:none;', root);
    // 물약 그림(public/assets/ui/items/hp_s·mp_s, 상점·가방과 같은 그림). 못 받으면 예전 병 SVG
    const art = el('img', 'width:100%;height:100%;object-fit:contain;display:block;filter:drop-shadow(0 2px 1px rgba(0,0,0,.6));', face);
    art.alt = ''; art.draggable = false; art.src = assetUrl('ui/items/' + s.art + '.webp');
    art.onerror = () => { face.innerHTML = flaskSvg(s.liquid, s.hi); };
    const sweep = el('div', 'position:absolute;inset:0;border-radius:50%;pointer-events:none;background:none;', root);
    const cd = el('div', 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;font:900 ' + Math.round(size * 0.34) + 'px ' + FONT_NUM + ';color:#fff;' + TEXT_OUTLINE, root, '');
    const count = el('div', 'position:absolute;right:-5px;bottom:-4px;min-width:20px;height:18px;padding:0 4px;box-sizing:border-box;border-radius:9px;' +
      'background:linear-gradient(180deg,#26335a,#10162a);border:1.5px solid ' + s.ring + ';font:900 11px ' + FONT_NUM + ';color:#fff;line-height:15px;text-align:center;pointer-events:none;text-shadow:0 1px 1px #000;', root, '0');
    count.dataset.testid = 'potion-count-' + idx;
    const decay = el('div', 'position:absolute;right:-6px;top:-6px;display:none;padding:0 4px;height:15px;border-radius:7px;background:#3a1418;border:1px solid #ff9a6a;' +
      'font:900 10px ' + FONT_NUM + ';color:#ffd0b0;line-height:13px;pointer-events:none;', root, '');
    if (this.mode === 'pc') {
      el('div', 'position:absolute;left:-4px;top:-4px;min-width:17px;height:17px;box-sizing:border-box;border-radius:5px;background:linear-gradient(180deg,#26335a,#10162a);border:1.5px solid ' + C.gold + ';' +
        'font:800 10px ' + FONT + ';color:' + C.gold + ';display:flex;align-items:center;justify-content:center;pointer-events:none;', root, s.key);
    }
    const b: Btn2 = { bit: s.bit, root, face, sweep, cd, count, decay, ring: s.ring, pointer: -1, lastSweep: -1, lastTenths: -1, lastCount: -1, lastDecay: -1, lastDim: -1 };
    root.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (b.pointer !== -1) return;
      b.pointer = e.pointerId;
      root.setPointerCapture(e.pointerId);
      root.style.transform = 'scale(.92)';
      this.input.buttonDown(s.bit);
    });
    const up = (e: PointerEvent): void => {
      if (e.pointerId !== b.pointer) return;
      b.pointer = -1;
      root.style.transform = '';
      this.input.buttonUp(s.bit);
    };
    root.addEventListener('pointerup', up);
    root.addEventListener('pointercancel', up);
    return b;
  }

  /** 터치: 슬롯 i의 중심(x, y)과 지름 d(px, 게임 화면 좌표) */
  place(i: number, x: number, y: number, d: number): void {
    const s = this.btns[i].root.style;
    s.width = d + 'px'; s.height = d + 'px';
    s.left = (x - d / 2).toFixed(1) + 'px'; s.top = (y - d / 2).toFixed(1) + 'px';
  }

  private calm(): boolean { return reducedMotion() || this.view.settings.reduceFlash; }
  private deny(slot: number): void {
    const b = this.btns[slot];
    if (!b) return;
    if (this.calm()) { b.root.animate([{ borderColor: '#ff5a5a' }, { borderColor: C.gold }], { duration: 260 }); return; }
    b.root.animate(DENY_KF, DENY_OPT);
  }
  private pop(slot: number): void {
    const b = this.btns[slot];
    if (b && !this.calm()) b.face.animate(POP_KF, POP_OPT);
  }

  /** 던전에서만 보인다. 쿨다운·보유 수·감쇠를 바뀔 때만 쓴다 */
  update(visible: boolean): void {
    const v = this.view;
    this.cursor = v.events.readFrom(this.cursor, this.onEvent);
    const on = visible && combatScene(v.flow.scene) ? 1 : 0;
    if (on !== this.shown) { this.shown = on; this.root.style.display = on ? (this.mode === 'pc' ? 'flex' : 'block') : 'none'; }
    if (!on) return;
    const c = v.consume, cons = v.gear.cons;
    if (this.scroll) {
      const n = Math.min(999, cons['scroll_return'] ?? 0);
      if (n !== this.lastScrolls) { this.lastScrolls = n; this.scrollCount.textContent = COUNT_TEXT[n]; this.scroll.style.filter = n > 0 ? '' : DIM_FILTER; }
      if (this.armedUntil > 0 && performance.now() >= this.armedUntil) { this.armedUntil = 0; this.scrollArm.style.display = 'none'; }
    }
    const sweep = sweepStep(c.potionCd, c.potionCdMax), tenths = cdTenths(c.potionCd);
    const decayPct = Math.round(c.decay * 100);
    for (let i = 0; i < this.btns.length; i++) {
      const b = this.btns[i];
      if (sweep !== b.lastSweep) { b.lastSweep = sweep; b.sweep.style.background = CD_SWEEP[sweep]; }
      if (tenths !== b.lastTenths) { b.lastTenths = tenths; b.cd.textContent = CD_TEXT[tenths]; }
      // 보유 수 = 이 칸 종류 전부(가장 큰 것부터 쓴다)
      let n = 0;
      for (let k = 0; k < POTIONS.length; k++) if ((POTIONS[k].kind === 'hp') === (i === 0)) n += cons[POTIONS[k].id] ?? 0;
      n = Math.min(999, n);
      if (n !== b.lastCount) { b.lastCount = n; b.count.textContent = COUNT_TEXT[n]; }
      const dim = n === 0 ? 1 : 0;
      if (dim !== b.lastDim) { b.lastDim = dim; b.face.style.filter = dim ? DIM_FILTER : ''; }
      if (decayPct !== b.lastDecay) {
        b.lastDecay = decayPct;
        const t = decayPct < 100 ? DECAY_TEXT[decayPct] : undefined;
        b.decay.style.display = t ? 'block' : 'none';
        if (t) b.decay.textContent = t;
      }
    }
  }
}
